import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  usePractice,
  getTimeSlotStatus,
} from "@/hooks/usePractice";
import { useFocus, getRotationIndex, rotateSlice } from "@/hooks/useFocus";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Check,
  X,
  Flame,
  Target,
  Plus,
  Trash2,
} from "lucide-react";
import { InstallBanner } from "@/components/InstallBanner";
import { FokusSheet } from "@/components/FokusSheet";
import { ALL_VOWS, type Vow } from "@/data/vows";
import { VowDetailDialog } from "@/components/VowDetailDialog";
import { useFavoritesStore } from "@/stores/useFavoritesStore";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const ZEHN_TUGENDEN = ALL_VOWS.filter((v) => v.category === "ten-virtues");

export function HomePage() {
  const navigate = useNavigate();
  const {
    todayEntries,
    completedSlots,
    streak,
    nowMinutes,
    timeSlots,
    loading,
  } = usePractice();

  const { activeFocus, addFocus, completeFocus, deleteFocus } = useFocus();

  const { isFavorite, toggleFavorite } = useFavoritesStore();
  const [selectedVow, setSelectedVow] = useState<Vow | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [fokusSheetOpen, setFokusSheetOpen] = useState(false);

  // Only active slots
  const activeSlots = useMemo(
    () => timeSlots.filter((ts) => ts.active),
    [timeSlots]
  );

  // Determine current slot id for Innehalten dropdown default
  const currentSlotId = useMemo(() => {
    const current = activeSlots.find(
      (ts) => getTimeSlotStatus(ts, completedSlots, nowMinutes) === "current"
    );
    return current ? String(current.slot) : activeSlots[0] ? String(activeSlots[0].slot) : "";
  }, [activeSlots, completedSlots, nowMinutes]);

  const [selectedSlotId, setSelectedSlotId] = useState(currentSlotId);

  // Keep dropdown in sync when the active slot changes (e.g. at slot boundary)
  useEffect(() => {
    setSelectedSlotId(currentSlotId);
  }, [currentSlotId]);

  const selectedSlotConfig = activeSlots.find((ts) => String(ts.slot) === selectedSlotId);

  // Today string (local, stable)
  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  // Rotation index for current date + selected slot
  const rotationIndex = useMemo(
    () => getRotationIndex(todayStr, parseInt(selectedSlotId || "1", 10)),
    [todayStr, selectedSlotId]
  );

  // Tugend rotation settings
  const tugendEnabled = useMemo(
    () => localStorage.getItem("vajra-tugend-rotation-enabled") !== "false",
    []
  );
  const rotatingTugend = useMemo(
    () => tugendEnabled ? ZEHN_TUGENDEN[rotationIndex % ZEHN_TUGENDEN.length] : null,
    [tugendEnabled, rotationIndex]
  );
  const tugendIndex = rotationIndex % ZEHN_TUGENDEN.length; // 0-based

  // Gelübde rotation from settings
  const gelübde = useMemo(() => {
    const enabled = localStorage.getItem("vajra-vow-slot-enabled") === "true";
    if (!enabled) return [];
    const categories: string[] = JSON.parse(
      localStorage.getItem("vajra-vow-slot-categories") || "[]"
    );
    const order = localStorage.getItem("vajra-vow-slot-order") || "sequential";
    const count = parseInt(localStorage.getItem("vajra-vow-slot-count") || "3", 10);
    // Exclude zehn-tugenden — they have their own rotation above
    const pool = ALL_VOWS.filter(
      (v) => categories.includes(v.category) && v.category !== "ten-virtues"
    );
    if (pool.length === 0) return [];
    if (order === "random") {
      // Deterministic shuffle seeded by rotationIndex
      const seed = rotationIndex;
      const shuffled = [...pool];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.abs((seed * (i + 1) * 1664525 + 1013904223) % (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled.slice(0, count);
    }
    return rotateSlice(pool, count, rotationIndex);
  }, [rotationIndex]);

  const today = new Date().toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const entryCount = todayEntries.length;

  if (loading) {
    return (
      <div className="px-4 py-6 space-y-6">
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  function fmtTime(h: number, m: number) {
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }

  function openDetail(vow: Vow) {
    setSelectedVow(vow);
    setDetailOpen(true);
  }

  function fmtFokusDate(dateStr: string) {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("de-DE", { day: "numeric", month: "long" });
  }

  function fmtDaysLeft(endDate: string) {
    const end = new Date(endDate + "T00:00:00");
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diff = Math.ceil((end.getTime() - now.getTime()) / 86400000);
    if (diff === 0) return "letzter Tag";
    if (diff === 1) return "noch 1 Tag";
    return `noch ${diff} Tage`;
  }

  function fmtFokusProgress(startDate: string, endDate: string): number {
    const start = new Date(startDate + "T00:00:00").getTime();
    const end = new Date(endDate + "T00:00:00").getTime();
    const now = Date.now();
    if (end <= start) return 0;
    return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
  }

  return (
    <div className="px-4 py-6 space-y-6">
      <InstallBanner />

      {/* 1. Slot pips — nur aktive */}
      {activeSlots.length > 0 && (
        <div className="flex gap-1.5">
          {activeSlots.map((ts) => {
            const status = getTimeSlotStatus(ts, completedSlots, nowMinutes);
            return (
              <div
                key={ts.slot}
                className={cn("h-2 flex-1 rounded-full transition-colors", {
                  "bg-emerald-500": status === "completed",
                  "bg-vajra-cyan": status === "current",
                  "bg-orange-500/70": status === "missed",
                  "bg-[#1E3A5F]": status === "upcoming",
                })}
              />
            );
          })}
        </div>
      )}

      {/* 2. Datum + Stats */}
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">{today}</p>
        <div className="flex items-center gap-3 text-sm">
          <span>
            <span className="font-semibold text-vajra-cyan">{entryCount}</span>{" "}
            {entryCount === 1 ? "Eintrag" : "Einträge"} heute
          </span>
          {streak > 0 && (
            <span className="flex items-center gap-1 text-orange-500 dark:text-orange-400">
              <Flame className="h-4 w-4" />
              <span className="font-semibold">{streak}</span>{" "}
              {streak === 1 ? "Tag" : "Tage"}
            </span>
          )}
        </div>
      </div>

      {/* 3. Positiv / Negativ Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => navigate("/entry", { state: { type: "positive" } })}
          className="rounded-xl border-2 border-emerald-500/30 bg-emerald-50/80 dark:bg-emerald-500/5 px-4 py-4 text-center transition-all hover:border-emerald-500/60 hover:bg-emerald-100 dark:hover:bg-emerald-500/10 active:scale-[0.97]"
        >
          <div className="flex flex-col items-center gap-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/20">
              <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-base font-semibold text-emerald-700 dark:text-emerald-400">Positiv</p>
            <p className="text-xs text-emerald-600/60 dark:text-emerald-400/50">Gutes getan</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => navigate("/entry", { state: { type: "negative" } })}
          className="rounded-xl border-2 border-rose-500/30 bg-rose-50/80 dark:bg-rose-500/5 px-4 py-4 text-center transition-all hover:border-rose-500/60 hover:bg-rose-100 dark:hover:bg-rose-500/10 active:scale-[0.97]"
        >
          <div className="flex flex-col items-center gap-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-500/20">
              <X className="h-5 w-5 text-rose-600 dark:text-rose-400" />
            </div>
            <p className="text-base font-semibold text-rose-700 dark:text-rose-400">Negativ</p>
            <p className="text-xs text-rose-600/60 dark:text-rose-400/50">Fehler bemerkt</p>
          </div>
        </button>
      </div>

      {/* 3b. Meditation */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => navigate("/entry", { state: { type: "meditation" } })}
          className="flex items-center gap-2.5 rounded-xl border border-vajra-cyan/25 bg-vajra-cyan/5 px-3.5 py-3 text-left hover:border-vajra-cyan/40 hover:bg-vajra-cyan/5 dark:hover:bg-vajra-cyan/10 transition-colors active:scale-[0.97]"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-vajra-cyan/15">
            <span className="text-base">📝</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-vajra-cyan">Meditation</p>
            <p className="text-xs text-vajra-cyan/60 dark:text-vajra-cyan/50">eintragen</p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => navigate("/meditation")}
          className="flex items-center gap-2.5 rounded-xl border border-vajra-cyan/40 bg-vajra-cyan/10 dark:bg-vajra-cyan/10 px-3.5 py-3 text-left hover:bg-vajra-cyan/15 transition-colors active:scale-[0.97]"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-vajra-cyan/20">
            <span className="text-base">🧘</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-vajra-cyan">Timer</p>
            <p className="text-xs text-vajra-cyan/60 dark:text-vajra-cyan/50">mit Gong</p>
          </div>
        </button>
      </div>

      {/* 4. Innehalten — Dropdown ohne Kasten */}
      {activeSlots.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Innehalten
          </p>
          <Select value={selectedSlotId} onValueChange={setSelectedSlotId}>
            <SelectTrigger className="border-0 border-b border-[#1E3A5F] rounded-none px-0 shadow-none h-9 text-[15px] font-semibold focus:ring-0 focus:ring-offset-0 bg-transparent">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {activeSlots.map((ts) => {
                const status = getTimeSlotStatus(ts, completedSlots, nowMinutes);
                return (
                  <SelectItem key={ts.slot} value={String(ts.slot)}>
                    {ts.emoji} {ts.labelShort} · {fmtTime(ts.startHour, ts.startMinute)} – {fmtTime(ts.endHour, ts.endMinute)}
                    {status === "current" ? " · Jetzt" : ""}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {selectedSlotConfig && (
            <p className="text-xs text-vajra-cyan">
              {fmtTime(selectedSlotConfig.startHour, selectedSlotConfig.startMinute)} – {fmtTime(selectedSlotConfig.endHour, selectedSlotConfig.endMinute)}
            </p>
          )}
        </div>
      )}

      {/* 5. Fokus */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Fokus
          </p>
          {activeFocus.length > 0 && (
            <button
              type="button"
              onClick={() => setFokusSheetOpen(true)}
              className="flex h-6 w-6 items-center justify-center rounded-full text-purple-500 hover:bg-purple-500/10 transition-colors"
              aria-label="Weiteren Fokus hinzufügen"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>

        {activeFocus.length === 0 ? (
          <button
            type="button"
            onClick={() => setFokusSheetOpen(true)}
            className="w-full flex items-center gap-3 rounded-xl border border-dashed border-purple-500/25 px-4 py-3.5 text-left hover:border-purple-500/40 hover:bg-purple-500/5 transition-colors"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-purple-500/10">
              <Target className="h-4 w-4 text-purple-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Fokus setzen</p>
              <p className="text-xs text-muted-foreground/60 mt-0.5">Vorsatz + Tugenden für einen Zeitraum</p>
            </div>
            <span className="text-purple-400/40 text-lg">›</span>
          </button>
        ) : (
          <div className="space-y-2">
            {activeFocus.map((f) => {
              const progress = fmtFokusProgress(f.startDate, f.endDate);
              const selectedVirtues = ZEHN_TUGENDEN.filter((t) => f.virtueIds.includes(t.id));
              return (
                <div
                  key={f.id}
                  className="rounded-xl border border-purple-500/30 bg-purple-500/5 p-3.5"
                >
                  <div className="flex items-start justify-between gap-2 mb-2.5">
                    <div className="flex items-start gap-2.5 flex-1 min-w-0">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-purple-500/15 mt-0.5">
                        <Target className="h-3.5 w-3.5 text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">{f.text}</p>
                        <p className="text-xs text-purple-500 dark:text-purple-400 mt-1">
                          {fmtDaysLeft(f.endDate)} · bis {fmtFokusDate(f.endDate)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => completeFocus(f.id)}
                        className="p-1 text-gray-400 hover:text-emerald-400 transition-colors"
                        aria-label="Fokus abschließen"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteFocus(f.id)}
                        className="p-1 text-gray-400 hover:text-rose-400 transition-colors"
                        aria-label="Fokus löschen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="h-1 rounded-full bg-purple-500/15 mb-2.5">
                    <div
                      className="h-1 rounded-full bg-purple-500 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>

                  {/* Tugenden chips */}
                  {selectedVirtues.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedVirtues.map((t) => (
                        <span
                          key={t.id}
                          className="rounded-full border border-purple-500/25 bg-purple-500/10 px-2 py-0.5 text-[11px] text-purple-400"
                        >
                          {t.de.title}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 6. Tugend (1 rotierend) */}
      {rotatingTugend && (
        <>
          <div className="border-t border-[#1E3A5F]/60" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Tugend{" "}
              <span className="normal-case font-normal text-vajra-cyan/50">
                ↻ {tugendIndex + 1}/10
              </span>
            </p>
            <button
              type="button"
              onClick={() => openDetail(rotatingTugend)}
              className="w-full flex items-start gap-2.5 rounded-xl bg-vajra-cyan/5 border border-vajra-cyan/15 px-3 py-2.5 text-left hover:bg-vajra-cyan/10 transition-colors"
            >
              <span className="mt-[5px] h-1.5 w-1.5 rounded-full bg-vajra-cyan shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">
                  {rotatingTugend.de.title}
                </p>
                {rotatingTugend.de.positiveFormulation && (
                  <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
                    {rotatingTugend.de.positiveFormulation}
                  </p>
                )}
              </div>
              <span className="text-vajra-cyan/40 text-base mt-0.5 shrink-0">›</span>
            </button>
          </div>
        </>
      )}

      {/* 7. Gelübde — mit Trennlinie abgesetzt */}
      {gelübde.length > 0 && (
        <>
          <div className="border-t border-[#1E3A5F]/60" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Gelübde{" "}
              <span className="normal-case font-normal text-vajra-blue/50">
                ↻ Innehalten {selectedSlotId}
              </span>
            </p>
            <div className="space-y-2">
              {gelübde.map((vow) => (
                <button
                  key={vow.id}
                  type="button"
                  onClick={() => openDetail(vow)}
                  className="w-full flex items-start gap-2.5 rounded-xl bg-vajra-blue/5 border border-vajra-blue/15 px-3 py-2.5 text-left hover:bg-vajra-blue/10 transition-colors"
                >
                  <span className="mt-[5px] h-1.5 w-1.5 rounded-full bg-vajra-blue shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">
                      {vow.de.title}
                    </p>
                    {vow.de.positiveFormulation && (
                      <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
                        {vow.de.positiveFormulation}
                      </p>
                    )}
                  </div>
                  <span className="text-vajra-blue/40 text-base mt-0.5 shrink-0">›</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <VowDetailDialog
        vow={selectedVow}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        isFavorite={selectedVow ? isFavorite(selectedVow.id) : false}
        onToggleFavorite={toggleFavorite}
      />

      <FokusSheet
        open={fokusSheetOpen}
        onOpenChange={setFokusSheetOpen}
        onSave={addFocus}
      />
    </div>
  );
}
